import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { streamingServiceApi, EstreamingServiceStatus, type StreamingService } from '@/services/streaming-service';
import { StatusBadge } from '@/components/dashboard/StatusBadge';
import { RegisterStreamingServiceModal } from '@/components/streaming-services/RegisterStreamingServiceModal';
import { EditStreamingServiceModal } from '@/components/streaming-services/EditStreamingServiceModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Search, MoreVertical, Eye, Edit, DollarSign, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn, formatCurrency } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useUrlFilters, useUrlPage, useUrlSearch } from '@/hooks/use-url-filters';
interface ServiceStats {
  totalAccounts: number;
  totalSlots: number;
  usedSlots: number;
  activeSubscriptions: number;
  monthlyRevenue: number;
  avgPrice:number
}

function getServiceStats(service: StreamingService): ServiceStats {
  const availableSlots = service.availableSlotsCount ?? 0;
  const usedSlots = service.occupiedSlotsCount ?? 0;

  return {
    totalAccounts: service.accountsCount ?? 0,
    totalSlots: availableSlots + usedSlots,
    usedSlots,
    activeSubscriptions: service.activeSubscriptionsCount ?? 0,
    monthlyRevenue: service.monthlyRevenue ?? 0,
    avgPrice: service.avgPrice ?? 0,
  };
}

const PAGE_SIZE = 12;

const DEFAULT_FILTERS = {
  status: EstreamingServiceStatus.ACTIVE as string,
};

function generatePaginationNumbers(currentPage: number, totalPages: number): (number | string)[] {
  const delta = 1;
  const range: (number | string)[] = [];

  for (let i = 1; i <= totalPages; i++) {
    if (
      i === 1 ||
      i === totalPages ||
      (i >= currentPage - delta && i <= currentPage + delta)
    ) {
      range.push(i);
    } else if (range[range.length - 1] !== '...') {
      range.push('...');
    }
  }

  return range;
}

function ServiceCard({
  service,
  onEdit,
}: {
  service: StreamingService;
  onEdit: (service: StreamingService) => void;
}) {
  const stats = getServiceStats(service);
  const usagePercent = stats.totalSlots > 0 ? (stats.usedSlots / stats.totalSlots) * 100 : 0;

  return (
    <Link to={`/services/${service.id}`} className="block">
      <div className="card-elevated p-5 hover:shadow-md transition-all cursor-pointer group">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
              style={{ backgroundColor: `${service.color}15` }}
            >
              {service.icon}
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                  {service.name}
                </h3>
                <span className="text-xs font-mono text-muted-foreground">#{service.id}</span>
              </div>
              <StatusBadge status={service.status} />
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.preventDefault()}>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem>
                <Eye className="h-4 w-4 mr-2" />
                View Details
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.preventDefault();
                  onEdit(service);
                }}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Service
              </DropdownMenuItem>
              <DropdownMenuItem>
                <DollarSign className="h-4 w-4 mr-2" />
                Manage Pricing
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-xs text-muted-foreground">Accounts</p>
            <p className="font-semibold text-foreground">{stats.totalAccounts}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Subscriptions</p>
            <p className="font-semibold text-foreground">{stats.activeSubscriptions}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Avg Price</p>
            <p className="font-semibold text-foreground">${formatCurrency(stats.avgPrice)}/mo</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Revenue</p>
            <p className="font-semibold text-success">${formatCurrency(stats.monthlyRevenue)}/mo</p>
          </div>
        </div>

        {/* Slot Usage */}
        <div>
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-muted-foreground">Slot Usage</span>
            <span className="font-medium text-foreground">
              {stats.usedSlots}/{stats.totalSlots}
            </span>
          </div>
          <Progress
            value={usagePercent}
            className={cn(
              "h-2",
              usagePercent >= 90 ? "[&>div]:bg-destructive" :
              usagePercent >= 70 ? "[&>div]:bg-warning" : ""
            )}
          />
        </div>
      </div>
    </Link>
  );
}

export default function Services() {
  const [searchQuery, setSearchQuery, debouncedSearch] = useUrlSearch('search');
  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS);
  const statusFilter = filters.status as EstreamingServiceStatus | 'ALL';
  const [currentPage, setCurrentPage] = useUrlPage();
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [editingService, setEditingService] = useState<StreamingService | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Obtener servicios de streaming desde el API
  const { data: servicesResponse, isLoading, error, isFetching } = useQuery({
    queryKey: ['streaming-services', currentPage, debouncedSearch, statusFilter],
    queryFn: () => streamingServiceApi.getAll({
      page: currentPage,
      limit: PAGE_SIZE,
      status: statusFilter === 'ALL' ? undefined : statusFilter,
      name: debouncedSearch || undefined,
    }),
  });

  const streamingServices = servicesResponse?.data || [];
  const pagination = servicesResponse?.pagination;
  const totalPages = pagination?.totalPages || 1;
  const totalItems = pagination?.totalItems || 0;

  const totalStats = streamingServices.reduce(
    (acc, service) => {
      const stats = getServiceStats(service);
      return {
        accounts: acc.accounts + stats.totalAccounts,
        slots: acc.slots + stats.totalSlots,
        used: acc.used + stats.usedSlots,
        revenue: acc.revenue + stats.monthlyRevenue,
      };
    },
    { accounts: 0, slots: 0, used: 0, revenue: 0 }
  );

  return (
    <DashboardLayout title="Services" subtitle="Manage streaming platforms and their accounts">
      <div className="space-y-6 animate-fade-in">
        {/* Error State */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>
              Error al cargar los servicios. Por favor, intenta de nuevo.
            </AlertDescription>
          </Alert>
        )}

        {/* Summary Bar */}
        <div className="flex flex-wrap items-center gap-4 p-4 card-elevated">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Total Services:</span>
            <span className="font-semibold text-foreground">{totalItems}</span>
          </div>
          <div className="w-px h-5 bg-border hidden sm:block" />
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Total Accounts:</span>
            <span className="font-semibold text-foreground">{totalStats.accounts}</span>
          </div>
          <div className="w-px h-5 bg-border hidden sm:block" />
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Total Slots:</span>
            <span className="font-semibold text-foreground">{totalStats.slots}</span>
          </div>
          <div className="w-px h-5 bg-border hidden sm:block" />
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Monthly Revenue:</span>
            <span className="font-semibold text-success">$ {formatCurrency(totalStats.revenue)}</span>
          </div>
        </div>

        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-3 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search services..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select
              value={statusFilter}
              onValueChange={(value) => setFilters({ status: value })}
            >
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filtrar por estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos los estados</SelectItem>
                <SelectItem value={EstreamingServiceStatus.ACTIVE}>Activos</SelectItem>
                <SelectItem value={EstreamingServiceStatus.INACTIVE}>Inactivos</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button className="gap-2" onClick={() => setIsRegisterModalOpen(true)}>
            <Plus className="h-4 w-4" />
            Add Service
          </Button>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="card-elevated p-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            <p className="text-muted-foreground mt-2">Cargando servicios...</p>
          </div>
        )}

        {/* Services Grid */}
        {!isLoading && (
          <div className="relative">
            {isFetching && !isLoading && (
              <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10 rounded-lg">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {streamingServices.map((service) => (
                <ServiceCard
                  key={service.id}
                  service={service}
                  onEdit={(s) => {
                    setEditingService(s);
                    setIsEditModalOpen(true);
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {!isLoading && streamingServices.length === 0 && (
          <div className="card-elevated p-8 text-center">
            <p className="text-muted-foreground">No services found</p>
            <p className="text-sm text-muted-foreground mt-1">Try adjusting your search or add a new service</p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4">
            <p className="text-sm text-muted-foreground">
              Mostrando {streamingServices.length} de {totalItems} servicios
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1 || isFetching}
              >
                <ChevronLeft className="h-4 w-4" />
                Anterior
              </Button>
              <div className="flex items-center gap-1">
                {generatePaginationNumbers(currentPage, totalPages).map((page, idx) => (
                  page === '...' ? (
                    <span key={`ellipsis-${idx}`} className="px-2 text-muted-foreground">...</span>
                  ) : (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      className="w-9"
                      onClick={() => setCurrentPage(page as number)}
                      disabled={isFetching}
                    >
                      {page}
                    </Button>
                  )
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages || isFetching}
              >
                Siguiente
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Register Modal */}
      <RegisterStreamingServiceModal
        open={isRegisterModalOpen}
        onOpenChange={setIsRegisterModalOpen}
      />

      {/* Edit Modal */}
      <EditStreamingServiceModal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        service={editingService}
      />
    </DashboardLayout>
  );
}
